/**
 * The invaders' animations (GDD section 7), cut out of the GDD's sheets (pages 11-13) into src/assets/invaders.png:
 * one row per invader (INVADERS order), 18 frames of 20 × 20 cells, background transparent:
 * idle 4 frames at 5 fps, walk 4 at 10 fps, damage 4 at 10 fps, death 6 at 5 fps. The sprites face right, and the
 * 16 × 16 body box is at (2, 4) in the frame (feet on its last row). Display only: nothing goes back into the game.
 */
import { FP, UNIT_W, type GameEvent, type GameState, type Unit } from '@invaders/game';
import atlasUrl from '../assets/invaders.png';
import type { Camera } from './camera';

export const FRAME = 20;
const BODY_X = 2;
const BODY_Y = 4;

interface Anim { readonly first: number; readonly count: number; readonly ms: number }
const IDLE: Anim = { first: 0, count: 4, ms: 200 };
const WALK: Anim = { first: 4, count: 4, ms: 100 };
const HURT: Anim = { first: 8, count: 4, ms: 100 };
const DEATH: Anim = { first: 12, count: 6, ms: 200 };

const atlas = new Image();
atlas.src = atlasUrl;
/** Resolved once the sheet is loaded (or failed to): the loading screen waits for it. Without `decode` (the stand-in
 *  Image of the tests, an old browser), resolved at once. */
export const spritesReady: Promise<void> = typeof atlas.decode === 'function' ? atlas.decode().catch(() => {}) : Promise.resolve();

/** Draws frame `frame` of invader `kind` with its body box's top-left corner at cell (x, y); false before the sheet loads. */
export function drawFrame(ctx: CanvasRenderingContext2D, cam: Camera, kind: number, frame: number, x: number, y: number, facing: number, alpha = 1): boolean {
  if (!atlas.complete || atlas.naturalWidth === 0) return false;
  const s = cam.scale;
  const sx = (x - BODY_X - cam.x) * s;
  const sy = (y - BODY_Y - cam.y) * s;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = false;
  if (facing < 0) {
    // Mirrored around the frame's middle, which is the body's middle.
    ctx.translate(sx + FRAME * s, sy);
    ctx.scale(-1, 1);
    ctx.drawImage(atlas, frame * FRAME, kind * FRAME, FRAME, FRAME, 0, 0, FRAME * s, FRAME * s);
  } else {
    ctx.drawImage(atlas, frame * FRAME, kind * FRAME, FRAME, FRAME, sx, sy, FRAME * s, FRAME * s);
  }
  ctx.restore();
  return true;
}

interface Dying { kind: number; x: number; y: number; facing: number; start: number }

/**
 * Which animation each unit plays: damage when it was hurt (`damage` event), walk while it walks, idle otherwise
 * (still when frozen), and the death animation where it fell (`death` event; not when it left the map).
 */
export class UnitAnimator {
  private hurt = new Map<number, number>();
  private dying: Dying[] = [];
  private state: GameState | null = null;

  onEvents(state: GameState, events: readonly GameEvent[], now: number): void {
    if (state !== this.state) { this.state = state; this.hurt.clear(); this.dying = []; }
    for (const e of events) {
      if (e.type === 'damage' && e.amount > 0) this.hurt.set(e.unit, now);
      else if (e.type === 'death' && !e.offMap) {
        const u = state.units[e.unit];
        if (u) this.dying.push({ kind: u.kind, x: u.x / FP, y: u.y / FP, facing: u.facing, start: now });
      }
    }
  }

  frameOf(unit: Unit, now: number): number {
    const hurtAt = this.hurt.get(unit.id);
    if (hurtAt !== undefined) {
      const k = Math.floor((now - hurtAt) / HURT.ms);
      if (k < HURT.count) return HURT.first + k;
      this.hurt.delete(unit.id);
    }
    if (unit.frozen !== 0) return IDLE.first;
    // Each unit has its own phase, so that a team does not breathe in step.
    if (unit.walkDir !== 0 || unit.slide > 0) return WALK.first + (Math.floor(now / WALK.ms) + unit.id) % WALK.count;
    return IDLE.first + (Math.floor(now / IDLE.ms) + unit.id * 3) % IDLE.count;
  }

  /** A living unit, at its interpolated position. */
  draw(ctx: CanvasRenderingContext2D, cam: Camera, unit: Unit, now: number, alpha = 1): void {
    drawFrame(ctx, cam, unit.kind, this.frameOf(unit, now), unit.x / FP, unit.y / FP, unit.facing, alpha);
  }

  /** The death animations running. */
  drawDeaths(ctx: CanvasRenderingContext2D, cam: Camera, now: number): void {
    this.dying = this.dying.filter((d) => now - d.start < DEATH.count * DEATH.ms);
    for (const d of this.dying) drawFrame(ctx, cam, d.kind, DEATH.first + Math.floor((now - d.start) / DEATH.ms), d.x, d.y, d.facing);
  }
}

/** GDD HUD mockups: a thin bar in the team colour over the head, 12 cells wide, the empty part dark. */
export function drawHealthBar(ctx: CanvasRenderingContext2D, cam: Camera, unit: Unit, color: string, maxHp: number): void {
  const s = cam.scale;
  const width = 12;
  const sx = (unit.x / FP + (UNIT_W - width) / 2 - cam.x) * s;
  const sy = (unit.y / FP - 3 - cam.y) * s;
  const full = Math.max(0, Math.min(1, unit.hp / maxHp));
  ctx.fillStyle = 'rgba(40, 40, 52, 0.85)';
  ctx.fillRect(sx, sy, width * s, s);
  ctx.fillStyle = color;
  ctx.fillRect(sx, sy, Math.round(width * full) * s, s);
}
